/**
 * Copyright Highway9 Networks Inc.
 */
import React, { useEffect, useState } from "react";
import { authenticationService } from "../services";
import useErrorsAndWarnings from "../hooks/useErrorsAndWarnings";
import { ErrorsComponent, CircularProgress, LoginView } from "@h9/ui-lib";
import  { ConfigFor2FA } from "@h9/ui-lib/components/organisms/LoginView";

export function LoginPage() {
  const { errors, warnings, setErrorsAndWarnings, closeAlert, closeAll } = useErrorsAndWarnings();
  const [loading, setLoading] = useState(true);
  const [loadingButton, setLoadingButton] = React.useState(false);
  const [hidePassword, setHidePassword] = useState(true);
  const [lastUsedEmail, setLastUsedEmail] = useState("");
  const [show2FAInput, setShow2FAInput] = useState<boolean>(false);
  const [configFor2FA, setConfigFor2FA] = useState<ConfigFor2FA>({
    otpTargets: [],
    phoneNumber: "",
    email: "",
    status: "UNKNOWN",
  });

  async function handleSubmit(email: string, password: string, otpCode?: string) {
    setLoadingButton(true);

    setConfigFor2FA({
      otpTargets: [],
      email: "",
      phoneNumber: "",
      status: "UNKNOWN",
    });

    setErrorsAndWarnings([], []);

    try {
      const sessionInfo = await authenticationService.getSessionInfo(email.trim());
      if (!sessionInfo) {
        return;
      }
      const loginResponse = await authenticationService.login(email.trim(), password.trim(), sessionInfo.authKey, otpCode);
      const otpStatus = loginResponse?.otpInfo?.otpValidationStatus;

      if (otpStatus) {
        setShow2FAInput(true);
        setConfigFor2FA({ ...configFor2FA, status: otpStatus });
        return;
      }

      if (otpStatus === "PENDING") {
        setConfigFor2FA({ ...configFor2FA, email: loginResponse?.email || "", phoneNumber: loginResponse?.otpInfo?.phoneNumber || "" });
        return;
      }

      if (
        otpStatus === "MISSING_PHONE_NUMBER" ||
        otpStatus === "EXPIRED" ||
        otpStatus === "FAILED" ||
        otpStatus === "FAILED_TOO_FREQUENT"
      ) {
        setConfigFor2FA({ ...configFor2FA, status: otpStatus });
        return;
      }

      console.log("Logged in.");
      // Remember the email for next login
      localStorage.setItem("email", email);

      const url = sessionStorage.getItem("url") || "/";
      window.location.replace(url);
    } catch (error : any) {
      console.error(error);
      setErrorsAndWarnings(error.errors, error.warnings);
    } finally {
      setLoadingButton(false);
    }
  }

  async function redirectToCsp(email: string) {
    // replace vmc.live.highway9networks.com with console.live.highway9networks.com means find the current hostname and replace vmc with console
    // the url may also look like https://vmc.us-10.live.highway9networks.com
    // Also handles: vmc.demo.highway9networks.com, vmc.us-10.demo.highway9networks.com,
    // vmc.track1.test.highway9networks.com, vmc.us-10.track1.test.highway9networks.com
    let currentHostname = window.location.hostname;
    // Remove protocol if present (though hostname shouldn't have it)
    currentHostname = currentHostname.replace(/^https?:\/\//, "");
    const hostnameParts = currentHostname.split(".");
    if (hostnameParts.length < 2) {
      console.error("Invalid hostname format", currentHostname);
      return;
    }
    // Remove the first part (service name like "vmc")
    let remainingParts = hostnameParts.slice(1);
    // Check if the second part is a region prefix (e.g., "us-10", "eu-1") - pattern: letters-dash-numbers
    if (remainingParts.length > 0 && /^[a-z]+-\d+$/i.test(remainingParts[0])) {
      // Remove the region prefix
      remainingParts = remainingParts.slice(1);
    }
    // Build the console hostname: console + remaining parts
    const cspHostname = ["console", ...remainingParts].join(".");
    const redirectUrl = `https://${cspHostname}/vmcLogin?vmc=${window.location.href}&email=${email}`;
    window.location.href = redirectUrl;
  }

  async function handleNext(email: string) {
    setLoadingButton(true);
    try {
      const sessionInfo = await authenticationService.getSessionInfo(email);
      // Redirect to CSP when:
      // - authType is not PASSWORD (OIDC/SAML), or
      // - authType is PASSWORD and OTP 2FA is required (is2faenabled in CSP).
      // Otherwise PASSWORD users stay on VMC for UI workflows.
      if (sessionInfo?.authType !== "PASSWORD" || (sessionInfo?.authType === "PASSWORD" && sessionInfo.otpRequired)) {
        redirectToCsp(email);
      } else {
        setHidePassword(false);
      }
    } catch (error) {
      console.log("error getting session info", error);
      setHidePassword(false);
    } finally {
      setLoadingButton(false);
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: preserve existing hook dependency list
  useEffect(() => {
    setLoading(true);
    setTimeout(() => {
      authenticationService
        .getSession()
        .then((response) => {
          if (response.status === 200) {
            // Cache email here too so OIDC/SAML users (who land here via openVmcUrl, not handleSubmit) get "remember user" on next visit.
            try {
              const cachedUser = JSON.parse(localStorage.getItem("currentUser") || "{}");
              if (cachedUser?.email) {
                localStorage.setItem("email", cachedUser.email);
              }
            } catch (e) {
              console.log("could not cache email from currentUser", e);
            }
            const url = sessionStorage.getItem("url") || "/";
            console.log("url: ", url);
            window.location.replace(url);
            //window.location.reload();
          }
        })
        .catch((error) => {
          setLoading(false);
          setErrorsAndWarnings(error.errors, error.warnings);

          // Auto-trigger login for remembered user
          const cachedEmail = localStorage.getItem("email");
          if (cachedEmail) {
            setLastUsedEmail(cachedEmail);
            handleNext(cachedEmail);
          }
        });
    }, 200);
  }, []);

  if (loading) {
    return (
      <div style={{ height: "100vh", width: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress size={80} />
      </div>
    );
  }

  function handleForgotPassword() {
    let host = window.location.host;
    const protocol = window.location.protocol;
    // Remove protocol if present (though host shouldn't have it)
    host = host.replace(/^https?:\/\//, "");
    // Remove port if present
    host = host.split(":")[0];
    const hostnameParts = host.split(".");
    if (hostnameParts.length < 2) {
      console.error("Invalid hostname format", host);
      return;
    }
    // Remove the first part (service name like "vmc")
    let remainingParts = hostnameParts.slice(1);
    // Check if the second part is a region prefix (e.g., "us-10", "eu-1") - pattern: letters-dash-numbers
    if (remainingParts.length > 0 && /^[a-z]+-\d+$/i.test(remainingParts[0])) {
      // Remove the region prefix
      remainingParts = remainingParts.slice(1);
    }
    // Build the console hostname: console + remaining parts
    const consoleHost = ["console", ...remainingParts].join(".");
    const redirectUrl = `${consoleHost}/login`;

    window.open(`${protocol}//${redirectUrl}`);
  }

  function handleBackToLogin() {
    setShow2FAInput(false);
    setConfigFor2FA({
      otpTargets: [],
      email: "",
      phoneNumber: "",
      status: "UNKNOWN",
    });
  }

  return (
    <LoginView
      errorsComponent={
        <ErrorsComponent errorsData={errors} warningsData={warnings} closeAll={closeAll} closeAlert={closeAlert} />
      }
      lastUsedEmail={lastUsedEmail}
      usernameLocked={!hidePassword || !!lastUsedEmail}
      hidePassword={hidePassword}
      loading={loadingButton}
      onLogin={handleSubmit}
      onNext={handleNext}
      onBackToLogin={handleBackToLogin}
      onForgotPassword={handleForgotPassword}
      handleSwitchUser={() => {
        localStorage.removeItem("email");
        setLastUsedEmail("");
        setHidePassword(true);
      }}
      show2FAInput={show2FAInput}
      configFor2FA={configFor2FA}
    />
  );
}
